<template>
  <PrimaryModal @clickBackground="close()">
    <DashboardHeader :initDashboardUser="initialDashboardUser" @changeProgramLanguage="changeProgramLanguage" />
    <div
      v-if="dashboardModalStatePresenter.isAddingDashboardUser.value"
      id="area"
      class="modal-size box-border px-12 bg-gray-300 dark:bg-dark-900"
      style="position: relative"
    >
      <div class="h-full flex justify-center items-center">
        <LoadingPanel />
      </div>
    </div>

    <div
      v-else-if="dashboardModalStatePresenter.tabState.value == 'OVERVIEW'"
      class="modal-size box-border grid bg-gray-300 dark:bg-dark-900"
      style="user-select: none"
    >
      <div class="w-full p-8 grid grid-rows-2 gap-6">
        <div class="grid grid-cols-4 row-span-1 gap-6">
          <div class="grid col-span-1">
            <div class="grid gap-4 grid-cols-2 grid-rows-2">
              <StatusCard
                :fontSize="'text-3xl'"
                :label="$t('message.submission').toString()"
                :value="initialDashboardUser.status != null ? initialDashboardUser.status.submissions.toString() : '0'"
              />
              <StatusCard
                :fontSize="'text-3xl'"
                :label="$t('message.solvedProblem').toString()"
                :value="initialDashboardUser.status != null ? initialDashboardUser.status.solved.toString() : '0'"
              />
              <StatusCard
                v-if="weeklyRatingPresenter.userWeeklyRatingList.value[0].length > 0"
                :fontSize="'text-3xl'"
                :label="$t('message.rating').toString()"
                :value="
                  weeklyRatingPresenter.userWeeklyRatingList.value[0][
                    weeklyRatingPresenter.userWeeklyRatingList.value[0].length - 1
                  ].rating
                    .toFixed(2)
                    .toString()
                "
              />
              <StatusCard
                v-if="!(weeklyRatingPresenter.userWeeklyRatingList.value[0].length > 0)"
                :fontSize="'text-3xl'"
                :label="$t('message.rating').toString()"
                :value="'0.00'"
              />
              <StatusCard
                v-if="initialDashboardUser.lastSubmitDate.toLocaleString() == BEGIN_DATE"
                :fontSize="'text-3xl'"
                :label="$t('message.lastSubmitDate').toString()"
                :value="'---'"
              />
              <StatusCard
                v-if="initialDashboardUser.lastSubmitDate.toLocaleString() != BEGIN_DATE"
                :fontSize="'text-lg'"
                :label="$t('message.lastSubmitDate').toString()"
                :value="
                  initialDashboardUser.lastSubmitDate != null
                    ? initialDashboardUser.lastSubmitDate.toLocaleString()
                    : '---'
                "
              />
            </div>
          </div>
          <div class="w-full h-full col-span-3 grid grid-cols-3 gap-6">
            <div class="grid p-2 bg-white dark:bg-dark-400" style="border-radius: 1rem">
              <ProgrammingCategoryRatingsChart />
            </div>
            <div class="grid h-full p-2 bg-white dark:bg-dark-400" style="border-radius: 1rem">
              <WhatCategoryRatingsChart />
            </div>
            <div class="grid p-2 bg-white dark:bg-dark-400" style="border-radius: 1rem">
              <HowCategoryRatingsChart />
            </div>
          </div>
        </div>
        <div class="grid grid-cols-3 row-span-1 gap-6">
          <div class="grid col-span-2 shadow-md p-4 bg-white dark:bg-dark-400" style="border-radius: 1rem">
            <LanguageStatisticsChart />
          </div>
          <div class="grid col-span-1 p-4 bg-white dark:bg-dark-400" style="border-radius: 1rem">
            <SubmissionStatusChart />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="dashboardModalStatePresenter.tabState.value == 'DAILY'"
      class="modal-size box-border grid bg-gray-300 dark:bg-dark-900"
      style="user-select: none"
    >
      <div class="w-full p-8 grid grid-rows-2 gap-6">
        <div class="grid col-span-1 bg-white dark:bg-dark-400" style="border-radius: 1rem">
          <div class="bg-white dark:bg-dark-400" style="border-radius: 1rem">
            <HeatMap />
          </div>
        </div>
        <div class="grid grid-cols-3 row-span-1 gap-6">
          <div class="shadow-md p-3 bg-white dark:bg-dark-400" style="border-radius: 1rem">
            <DailyEffortsChart />
          </div>
          <div class="shadow-md p-3 bg-white dark:bg-dark-400" style="border-radius: 1rem">
            <SolvedProblemsChart />
          </div>
          <div class="shadow-md p-3 bg-white dark:bg-dark-400" style="border-radius: 1rem">
            <WeeklyRatingChart />
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="dashboardModalStatePresenter.tabState.value == 'COURSE'">
      <CourseHeader />
      <div class="course-modal-size box-border grid bg-gray-300 dark:bg-dark-900" style="user-select: none">
        <div class="w-full p-8 grid gap-6">
          <div class="grid col-span-1 bg-white dark:bg-dark-400" style="border-radius: 1rem">
            <div class="bg-white dark:bg-dark-400 shadow-md" style="border-radius: 1rem">
              <CourseHeatMap
                :course="currentCourse"
                :programLanguage="courseProgramLanguage"
                :user="initialDashboardUser"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </PrimaryModal>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, onBeforeUnmount, onMounted, onUnmounted } from 'vue'

import CourseHeatMap from './children/course/CourseHeatMap.vue'
import DailyEffortsChart from './children/daily/DailyEffortsChart.vue'
import HeatMap from './children/daily/HeatMap.vue'
import SolvedProblemsChart from './children/daily/SolvedProblemsChart.vue'
import HowCategoryRatingsChart from './children/overview/HowCategoryRatingsChart.vue'
import LanguageStatisticsChart from './children/overview/LanguageStatisticsChart.vue'
import ProgrammingCategoryRatingsChart from './children/overview/ProgrammingCategoryRatingsChart.vue'
import SubmissionStatusChart from './children/overview/SubmissionStatusChart.vue'

import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import DashboardHeader from '@/presentation/components/Dashboard/children/DashboardHeader.vue'
import StatusCard from '@/presentation/components/Dashboard/children/StatusCard.vue'
import CourseHeader from '@/presentation/components/Dashboard/children/course/CourseHeader.vue'
import WeeklyRatingChart from '@/presentation/components/Dashboard/children/daily/WeeklyRatingChart.vue'
import WhatCategoryRatingsChart from '@/presentation/components/Dashboard/children/overview/WhatCategoryRatingsChart.vue'
import LoadingPanel from '@/presentation/components/Loading/LoadingPanel.vue'
import PrimaryModal from '@/presentation/components/Modal/PrimaryModal.vue'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useCategoryRatingPresenter } from '@/presentation/composables/dashboard/useCategoryRatingPresenter'
import { useDashboardModalStatePresenter } from '@/presentation/composables/dashboard/useDashboardModalStatePresenter'
import { useHeatMapPresenter } from '@/presentation/composables/dashboard/useHeatMapPresenter'
import { useStatisticsPresenter } from '@/presentation/composables/dashboard/useStatisticsPresenter'
import { useWeeklyRatingPresenter } from '@/presentation/composables/dashboard/useWeeklyRatingPresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import { useCoursePresenter } from '@/presentation/composables/problem/useCoursePresenter'

export default defineComponent({
  components: {
    PrimaryModal,
    DashboardHeader,
    CourseHeader,
    StatusCard,
    LanguageStatisticsChart,
    HeatMap,
    WeeklyRatingChart,
    WhatCategoryRatingsChart,
    HowCategoryRatingsChart,
    ProgrammingCategoryRatingsChart,
    DailyEffortsChart,
    SolvedProblemsChart,
    SubmissionStatusChart,
    CourseHeatMap,
    LoadingPanel
  },
  setup(props, ctx) {
    const { user } = useAuthStatePresenter()
    const modalStatePresenter = useModalStatePresenter()
    const { displayLanguage, isLightMode } = useConfigStatePresenter()
    const dashboardModalStatePresenter = useDashboardModalStatePresenter()
    const statisticsPresenter = useStatisticsPresenter()
    const categoryRatingPresenter = useCategoryRatingPresenter()
    const heatMapPresenter = useHeatMapPresenter()
    const coursePresenter = useCoursePresenter()
    const weeklyRatingPresenter = useWeeklyRatingPresenter()

    const BEGIN_DATE = '1970/1/1 9:00:00'

    const initialDashboardUser = dashboardModalStatePresenter.initialDashboardUser.value ?? user.value

    // 初期化処理
    const initModalState = () => {
      coursePresenter.getCourseList(displayLanguage.value)
      const courseList = computed(() => Object.values(coursePresenter.courseList.value))

      for (let i = 0; i < courseList.value.length; i++) {
        // 全コースのTopic情報を取得する
        coursePresenter.getCourseById(displayLanguage.value, courseList.value[i].id)
      }

      dashboardModalStatePresenter.setDashboardProgramLanguage('All')
      dashboardModalStatePresenter.addDashboardUser(initialDashboardUser.id, displayLanguage.value, isLightMode())
      heatMapPresenter.getHeatMapData(initialDashboardUser.id, displayLanguage.value)
    }
    initModalState()

    // コースヒートマップで表示するコース
    const currentCourse = computed(() => {
      const sortedCourses = Object.values(coursePresenter.courseList.value).sort((a, b) => a.serial - b.serial)
      return sortedCourses[dashboardModalStatePresenter.courseSerial.value]
    })

    // データを表示するプログラム言語
    const courseProgramLanguage: ComputedRef<ProgramLanguageType | 'All'> = computed(
      () => dashboardModalStatePresenter.dashboardProgramLanguage.value
    )

    const close = () => {
      modalStatePresenter.setIsShowDashboardModal(false)
    }

    const hotKeys = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        close()
      }
    }

    onMounted(() => {
      document.addEventListener('keydown', hotKeys)
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', hotKeys)
    })

    onBeforeUnmount(() => {
      dashboardModalStatePresenter.removeDashboardUserList()
      weeklyRatingPresenter.removeUserWeeklyRating()
      statisticsPresenter.removeUserStatistics()
      categoryRatingPresenter.removeUserCategories()
    })

    // プログラム言語を変更する
    const changeProgramLanguage = async (programLanguage: ProgramLanguageType | 'All') => {
      await dashboardModalStatePresenter.setDashboardProgramLanguage(programLanguage)

      // ユーザーのカテゴリー評価を削除し、再度言語に基づいて取得する
      await categoryRatingPresenter.removeUserCategories()
      if (programLanguage == 'All') {
        await categoryRatingPresenter.addUserCategoryRating(initialDashboardUser.id)
      } else {
        await categoryRatingPresenter.addUserCategoryRatingByLanguage(initialDashboardUser.id, programLanguage)
      }
      await dashboardModalStatePresenter.reloadRadarCharts(displayLanguage.value, isLightMode())
    }

    return {
      coursePresenter,
      dashboardModalStatePresenter,
      weeklyRatingPresenter,
      close,
      changeProgramLanguage,
      BEGIN_DATE,
      initialDashboardUser,
      currentCourse,
      courseProgramLanguage
    }
  }
})
</script>

<style lang="postcss" scoped>
.modal-size {
  width: 90vw;
  height: 80vh;
}
.course-modal-size {
  width: 90vw;
  height: calc(80vh - 51px);
}
</style>
